import menuConfig from '../config/addContentMenu.json';
import templateConfig from '../config/addContentTemplates.json';
import { createElementNode, createTextNode } from './htmlEditorUtils.js';

const text = (value) => createTextNode(value);
const element = (type, attributes = {}, children = []) => (
  createElementNode(type, attributes, children)
);

const simpleTextContent = {
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  p: 'Paragraph text',
  span: 'Inline text',
  strong: 'Strong text',
  em: 'Emphasized text',
  small: 'Small text',
  blockquote: 'Quote text',
  pre: 'Preformatted text',
  code: 'code',
  a: 'Link text',
  figcaption: 'Caption',
  li: 'List item',
  th: 'Heading',
  td: 'Cell',
};

export const HTML_ELEMENT_GROUPS = [
  {
    title: 'Layout',
    items: ['div', 'section', 'main', 'header', 'footer', 'nav', 'aside', 'article', 'figure', 'figcaption'],
  },
  {
    title: 'Text',
    items: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'strong', 'em', 'small', 'blockquote', 'pre', 'code'],
  },
  {
    title: 'Links and media',
    items: ['a', 'img', 'video', 'audio', 'iframe'],
  },
  {
    title: 'Lists',
    items: ['ul', 'ol', 'li'],
  },
  {
    title: 'Tables',
    items: ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'],
  },
  {
    title: 'Utility elements',
    items: ['hr', 'br'],
  },
];

export const createHtmlElementTemplate = (tagName) => {
  const tag = String(tagName || 'div').toLowerCase();

  if (tag === 'img') {
    return element('img', { src: '', alt: 'Image', class: ['img-fluid'] }, []);
  }
  if (tag === 'iframe') {
    return element('iframe', {
      src: '',
      title: 'Embedded content',
      class: ['w-100'],
      loading: 'lazy',
    }, []);
  }
  if (tag === 'video' || tag === 'audio') {
    return element(tag, { controls: true, class: ['w-100'] }, []);
  }
  if (tag === 'ul' || tag === 'ol') {
    return element(tag, {}, [
      element('li', {}, [text('List item')]),
      element('li', {}, [text('List item')]),
    ]);
  }
  if (tag === 'table') {
    return element('div', { class: ['table-responsive'] }, [
      element('table', { class: ['table'] }, [
        element('thead', {}, [
          element('tr', {}, [element('th', {}, [text('Column')])]),
        ]),
        element('tbody', {}, [
          element('tr', {}, [element('td', {}, [text('Value')])]),
        ]),
      ]),
    ]);
  }
  if (tag === 'hr' || tag === 'br') {
    return element(tag, {}, []);
  }

  const content = simpleTextContent[tag];
  return element(tag, {}, content ? [text(content)] : []);
};

export const createTemplateNodeFromDefinition = (definition) => {
  if (typeof definition === 'string' || typeof definition === 'number') {
    return text(String(definition));
  }
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
    throw new Error('The configured template contains an invalid node.');
  }
  if (definition.type === 'text') {
    return text(String(definition.content || ''));
  }

  return element(
    definition.type,
    definition.attributes || {},
    (definition.children || []).map(createTemplateNodeFromDefinition)
  );
};

const configuredTemplates = (templateType) => {
  const definitions = templateConfig.templates?.[templateType] || {};
  return Object.entries(definitions).map(([title, definition]) => ({
    title,
    create: () => createTemplateNodeFromDefinition(definition),
  }));
};

const configuredMenuTemplates = (templateType) => (
  menuConfig.items
    .filter((item) => item.template?.type === templateType)
    .map((item) => {
      const definition = templateConfig.templates?.[templateType]?.[item.template.key];
      return {
        title: item.label,
        detail: item.detail,
        iconClass: item.iconClass,
        create: () => createTemplateNodeFromDefinition(definition),
      };
    })
);

// Preserve the established exports while keeping their structures in JSON.
export const BOOTSTRAP_CONTAINERS = configuredTemplates('bootstrap-container');
export const BOOTSTRAP_COMPONENTS = configuredTemplates('bootstrap-component');
export const SIMPLE_SECTION_TEMPLATES = configuredMenuTemplates('simple-section');
export const SIMPLE_CONTENT_TEMPLATES = configuredMenuTemplates('simple-content');
